import axios from 'axios'
import jwtDecode from 'jwt-decode'
import router from '@/router'

export default {
  /**
   * Logs user in
   * @param {Object} - payload with username and password, eg. { username: xxx, password: xxx }
   */
  login: async ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      commit('app/setLoading', { type: 'login', state: true }, { root: true })
      commit('app/setError', null, { root: true })
      axios
        .post(`${process.env.VUE_APP_URL}/admin/login`, payload)
        .then(response => {
          commit('setJwt', response.data.token)
          commit('setIsAuthenticated', true)

          const claims = jwtDecode(response.data.token)
          const user = {
            id: claims.uid,
            name: claims.name,
            email: claims.id,
            phoneNumber: claims.phone_number,
            isAdmin: claims.is_admin,
            isPlaceAdmin: claims.is_placeadmin,
            region: claims.region,
            zones: claims.zones,
            visibleZones: claims.visible_zones,
            operators: claims.operators,
            regions: claims.regions,
            place_types: claims.place_types,
            canSeeClientsPersonalData: claims.can_see_clients_personal_data,
            isFleetManager: claims.is_fleet_manager,
            isFleetManagerOnly: claims.is_fleet_manager_only,
            isWhitelistManagerOnly: claims.is_whitelist_manager_only,
            isWhitelistManager: claims.is_whitelist_manager,
            IsOwnerOrAdminOfBookablePlace:
              claims.is_owner_or_admin_of_bookable_place,
            roles: claims.roles,
            managesVisitorsForFleetUser: claims.manages_visitors_for_fleet_user,
            token: response.data.token,
            canViewParkingFines: claims.can_view_parking_fines,
            canCancelParkingFines: claims.can_cancel_parking_fines,
            canViewKiosks: claims.can_view_kiosks,
            canEditAvailabilityTable:
              claims.privileges.CAN_EDIT_PLACE_AVAILABILITY_TABLE_SCHEDULE ||
              (claims.is_admin && claims.zones),
            canViewBookings:
              claims.privileges.CAN_VIEW_BOOKINGS ||
              claims.is_admin ||
              claims.is_owner_or_admin_of_bookable_place ||
              claims.roles.includes('BOOKINGS_ADMIN') ||
              claims.roles.includes('BOOKINGS_ADMIN_NO_SESSIONS'),
            canViewCampaigns: claims.is_admin,
            canViewReports:
              claims.is_admin ||
              claims.is_placeadmin ||
              claims.is_fleet_manager ||
              claims.is_whitelist_manager,
            canViewHome:
              !claims.roles.includes('BOOKINGS_ADMIN') &&
              !claims.roles.includes('BOOKINGS_ADMIN_NO_SESSIONS') &&
              !claims.roles.includes('OPERATOR'),
            canViewParkings:
              claims.is_admin ||
              claims.is_placeadmin ||
              claims.roles.includes('BOOKINGS_ADMIN'),
            canEndParkingSessions: claims.privileges.CAN_END_PARKING_SESSION,
            hasOperatorRole: claims.roles.includes('OPERATOR'),
            // Jurmala day passes are municipality data: global admins (no region/zone
            // scoping), plus anyone explicitly granted the privilege. NOT every is_admin —
            // a region/zone admin is scoped to their own places and must not see them.
            canSeeJurmalaDayPasses:
              (claims.is_admin &&
                claims.region === '' &&
                claims.zones === '') ||
              claims.privileges.CAN_SEE_JURMALA_DAY_PASSES
          }
          commit('setUser', user)

          localStorage.setItem('user', JSON.stringify(user))
          localStorage.setItem('jwt', JSON.stringify(user.token))

          commit('whitelists/setWhitelists', [], { root: true })
          commit('fleets/setFleets', [], { root: true })

          commit(
            'app/setLoading',
            { type: 'login', state: false },
            { root: true }
          )
          commit('app/setError', null, { root: true })
          resolve()
        })
        .catch(error => {
          if (error.response) {
            if (error.response.status === 401) {
              dispatch('logout')
              commit(
                'app/setLoading',
                { type: 'login', state: false },
                { root: true }
              )
              commit(
                'app/setError',
                { message: 'Invalid e-mail or password.' },
                { root: true }
              )
              reject()
            }
          } else {
            commit(
              'app/setLoading',
              { type: 'login', state: false },
              { root: true }
            )
            commit(
              'app/setError',
              { message: 'Something went wrong. Please try again later!' },
              { root: true }
            )
            reject()
          }
        })
    })
  },
  /**
   * Returns uuid(magic link)
   * @param {String} - user email
   */
  createMagicLink({ commit, rootState }, email) {
    return new Promise((resolve, reject) => {
      commit(
        'app/setLoading',
        { type: 'createMagicLink', state: true },
        { root: true }
      )
      commit('app/setError', null, { root: true })
      try {
        const config = {
          headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
        }
        axios
          .post(
            `${process.env.VUE_APP_URL}/adminapi/magic-links`,
            { Username: email },
            config
          )
          .then(response => {
            const uuid = response.data.Uuid
            if (uuid) {
              resolve(uuid)
            }
            commit(
              'app/setLoading',
              { type: 'createMagicLink', state: false },
              { root: true }
            )
            commit('app/setError', null, { root: true })
          })
      } catch (error) {
        reject(error)
        commit(
          'app/setLoading',
          { type: 'createMagicLink', state: false },
          { root: true }
        )
        commit('app/setError', error, { root: true })
      }
    })
  },
  /**
   * Sends password reset info to specified email
   * @param {Object} - { Email: xxxx, Language: xx, Origin: 'eg. barking' }
   */
  resetPassword: async ({ commit, rootState }, payload) => {
    commit(
      'app/setLoading',
      { type: 'resetPassword', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${rootState.authentication.jwt}`,
          Web: 'snabb_dashboard'
        }
      }
      payload.Origin = 'barking'
      await axios
        .post(
          `${process.env.VUE_APP_URL}/admin/reset`,
          {
            Email: payload.Email,
            Language: payload.Language,
            Origin: payload.Origin
          },
          config
        )
        .then(response => {
          if (
            payload.PasswordResetLinkUuid &&
            payload.PasswordResetLinkUuid.length
          ) {
            commit(
              'users/setUserValue',
              { key: 'PasswordResetLinkUuid', value: '', userId: payload.Id },
              { root: true }
            )
          }
          commit(
            'users/setUserValue',
            {
              key: 'PasswordResetLink',
              value: response.data.ResetLink,
              userId: payload.Id
            },
            { root: true }
          )
          commit(
            'app/setSnackbar',
            {
              show: true,
              type: 'success',
              message: `Password reset instructions have been sent to ${payload.Email}`
            },
            { root: true }
          )
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
    } catch (error) {
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message: `Something went wrong! Please try again later.`
        },
        { root: true }
      )
      commit(
        'app/setLoading',
        { type: 'resetPassword', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Logs user out. Removes user info from local storage and state
   */
  logout: async ({ commit }) => {
    commit('setUser', undefined)
    commit('setJwt', null)
    commit('setIsAuthenticated', false)
    localStorage.removeItem('user')
    localStorage.removeItem('jwt')

    commit('whitelists/setWhitelists', [], { root: true })
    commit('fleets/setFleets', [], { root: true })
    commit('users/setUsers', [], { root: true })
    if (router.currentRoute.name === 'home') {
      router.push({ path: '/login' })
    }
  }
}
